import { adminApiAxios, apiAxios } from '@/api'

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { fetchWrapper } from '@/reducers/fetch'
import { StripeCoupon } from '@/actions/settings/billingActions'

export enum CatchUpBookkeepingStatusType {
  incomplete = 'incomplete',
  complete = 'complete',
}
export enum CatchUpBookkeepingStatusActor {
  admin = 'admin',
  system = 'system',
}
/** Backend pipeline phase for catch-up bookkeeping */
export type CubkPhase =
  | 'complete'
  | 'your_review'
  | 'bk_review'
  | 'bank_incompatible'

export interface CatchUpBookkeepingStatus {
  id: number
  userId: number
  updatedByUserId: number | null
  status: CatchUpBookkeepingStatusType
  actor: CatchUpBookkeepingStatusActor
  paymentRequired: boolean | null
  paidAt: string | null
  invoiceId: string | null
  /** Timestamp when bookkeeper requested user followup */
  followupRequestedAt: string | null
  completedAt: string | null
  celebrationSeenAt?: string | null
  /** Computed pipeline phase */
  phase: CubkPhase | null
  /** Total count of items qualifying a followup request (missing statements + clarification txns) */
  qualifyingItemsCount?: number
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

export interface CatchUpBookkeepingStatusState {
  [key: number]: CatchUpBookkeepingStatus
}

export const FETCH_USER_CATCH_UP_BOOKKEEPING_STATUS =
  'FETCH_USER_CATCH_UP_BOOKKEEPING_STATUS'

const catchUpBookkeepingStatusSlice = createSlice({
  name: 'catchUpBookkeepingStatus',
  initialState: {} as CatchUpBookkeepingStatusState,
  reducers: {
    receiveCatchUpBookkeepingStatus: (
      state,
      action: PayloadAction<CatchUpBookkeepingStatus>
    ) => {
      state[action.payload.userId] = action.payload
    },
  },
})

export default catchUpBookkeepingStatusSlice.reducer

export const { receiveCatchUpBookkeepingStatus } =
  catchUpBookkeepingStatusSlice.actions

export const fetchUserCatchUpBookkeepingStatus = () => {
  return fetchWrapper({
    fetchKey: FETCH_USER_CATCH_UP_BOOKKEEPING_STATUS,
    defaultErrorMessage: 'There was an error fetching user bookkeeping status.',
    fetchFunction: async (dispatch) => {
      const json = await apiAxios.get<CatchUpBookkeepingStatus>(
        '/finances/api/v2/catch_up_bookkeeping_status'
      )

      dispatch(receiveCatchUpBookkeepingStatus(json.data))
      return json.data
    },
  })
}

export const FETCH_CATCH_UP_BK_PAYMENT_REQUIREMENTS =
  'FETCH_CATCH_UP_BK_PAYMENT_REQUIREMENTS'
export const setCatchUpBookkeepingPaymentRequirements = () => {
  return fetchWrapper({
    fetchKey: FETCH_CATCH_UP_BK_PAYMENT_REQUIREMENTS,
    defaultErrorMessage: 'There was an error fetching user bookkeeping status.',
    fetchFunction: async (dispatch) => {
      const json = await apiAxios.put<CatchUpBookkeepingStatus>(
        '/finances/api/v2/catch_up_bookkeeping_status/payment'
      )

      dispatch(receiveCatchUpBookkeepingStatus(json.data))
      return json.data
    },
  })
}

export const MARK_CUBK_CELEBRATION_SEEN = 'MARK_CUBK_CELEBRATION_SEEN'
export const markCubkCelebrationSeen = () => {
  return fetchWrapper({
    fetchKey: MARK_CUBK_CELEBRATION_SEEN,
    defaultErrorMessage:
      'There was an error marking the CUBK celebration seen.',
    fetchFunction: async (dispatch) => {
      const json = await apiAxios.patch<CatchUpBookkeepingStatus>(
        '/finances/api/v2/catch_up_bookkeeping_status',
        { celebrationSeen: true }
      )

      dispatch(receiveCatchUpBookkeepingStatus(json.data))
      return json.data
    },
  })
}

export const FETCH_NEEDS_TWO_CHECKOUTS = 'FETCH_NEEDS_TWO_CHECKOUTS'
export const fetchNeedsTwoCheckouts = () => {
  return fetchWrapper({
    fetchKey: FETCH_NEEDS_TWO_CHECKOUTS,
    fetchFunction: async (_dispatch) => {
      const json = await apiAxios.get<boolean>(
        '/finances/api/v2/catch_up_bookkeeping_status/separate_checkout'
      )
      return json.data
    },
  })
}

export type CatchUpBKMonths = {
  monthsThisYear: number
  monthsLastYear: number
  totalMonths: number
}
export const FETCH_CATCH_UP_BK_MONTHS = 'FETCH_CATCH_UP_BK_MONTHS'
export const fetchCatchUpBKMonths = () => {
  return fetchWrapper({
    fetchKey: FETCH_CATCH_UP_BK_MONTHS,
    fetchFunction: async (_dispatch) => {
      const json = await apiAxios.get<CatchUpBKMonths>(
        '/finances/api/v2/catch_up_bookkeeping_status/months'
      )
      return json.data
    },
    handleError: (_dispatch) => ({
      monthsSoFar: 0,
      monthsLastYear: 0,
      totalMonths: 0,
    }),
  })
}

/** Mirrors `SignUpTaxPackageTerms` in apps/api/src/api/component/promotions/sign_up_tax_package.service.ts and must change with it. */
export type SignUpTaxPackageTerms = {
  payroll: {
    freeMonths: number | null
    percentOff: number | null
    amountOffInCents: number | null
    monthlyPriceInCents: number
  } | null
  taxAdvisory: {
    percentOff: number | null
  } | null
}

export type CatchUpBkCouponResponse = StripeCoupon & {
  promoExpiresAt?: string
  taxPackage?: SignUpTaxPackageTerms
}

export const FETCH_CATCH_UP_BK_COUPON = (
  isAnnual: boolean,
  productId?: string
) =>
  productId
    ? `FETCH_CATCH_UP_BK_COUPON_IS_ANNUAL_${isAnnual}_PRODUCT_${productId}`
    : `FETCH_CATCH_UP_BK_COUPON_IS_ANNUAL_${isAnnual}`
export const fetchCatchUpBKCoupon = (isAnnual: boolean, productId?: string) => {
  return fetchWrapper({
    fetchKey: FETCH_CATCH_UP_BK_COUPON(isAnnual, productId),
    fetchFunction: async (_dispatch) => {
      const json = await apiAxios.get<CatchUpBkCouponResponse>(
        '/finances/api/v2/catch_up_bookkeeping/coupon',
        { params: productId ? { isAnnual, productId } : { isAnnual } }
      )
      return json.data
    },
  })
}

export const adminUpdateUserCatchUpBookkeepingStatus = (
  userId: number,
  status: CatchUpBookkeepingStatusType
) => {
  return fetchWrapper({
    defaultErrorMessage: 'Error updating user bookkeeping status',
    fetchFunction: async (dispatch) => {
      const json = await adminApiAxios.patch<CatchUpBookkeepingStatus>(
        `/finances/api/v2/admin/catch_up_bookkeeping_status/${userId}`,
        { status }
      )

      dispatch(receiveCatchUpBookkeepingStatus(json.data))
      return json.data
    },
  })
}

export const adminRequestCubkFollowup = (userId: number) => {
  return fetchWrapper({
    defaultErrorMessage: 'Failed to request catch-up bookkeeping followup',
    fetchFunction: async (dispatch) => {
      const json = await adminApiAxios.post<CatchUpBookkeepingStatus>(
        `/finances/api/v2/admin/catch_up_bookkeeping_status/${userId}/request_followup`
      )

      dispatch(receiveCatchUpBookkeepingStatus(json.data))
      return json.data
    },
  })
}

export const adminFetchUserCubkStatus = (userId: number) => {
  return fetchWrapper({
    fetchFunction: async (_dispatch) => {
      const json = await adminApiAxios.get<CatchUpBookkeepingStatus>(
        `/finances/api/v2/admin/catch_up_bookkeeping_status/${userId}`
      )
      return json.data
    },
  })
}
